import {Component, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {catchError, finalize, throwError} from 'rxjs';
import {StateService} from 'src/app/services/state.service';
import {User, UserProfileResponse, UserRole, UserMaxLen, USER_INIT, ValidateNewUser} from 'src/app/models/user';
import {HttpHeaders} from '@angular/common/http';
import {AgencySelectionComponent} from '../agency-selection/agency-selection.component';
import {HttpService} from 'src/app/services/http.service';
import {Agencies} from '../../models/agencies';
import {ActivatedRoute, Router} from '@angular/router';
import {UtilService} from 'src/app/services/util.service';
import { Location } from '@angular/common';
import {UserService} from "../../services/user.service";
import PlaceResult = google.maps.places.PlaceResult;

interface RequestInfo {
  url: string;
  request: any;
}

@Component({
    selector: 'app-manage-profile',
    templateUrl: './manage-profile.component.html',
    styleUrls: ['./manage-profile.component.scss'],
    standalone: false
})
export class ManageProfileComponent implements OnInit {
  @ViewChild('fProfile', { read: NgForm }) profileForm!: NgForm;
  @ViewChild('agencies') agencyComponent?: AgencySelectionComponent;

  disableForm = true;
  UserRole: typeof UserRole = UserRole;
  UserMaxLen: typeof UserMaxLen = UserMaxLen;
  selectedAgencies!: Agencies;
  firmNameLabel = "Name of Firm";
  userProfile: User = USER_INIT;
  savedProfile!: User;
  profileSaving = false;
  isChecked = false;
  agencyRoles = [{
    label: "AGENCY POC",
    id: 5
  }, {
    label: "AGENCY REP",
    id: 6
  }];

  userId: number | undefined;
  accountTypeId: number | undefined;
  register: boolean = false;

  protesterFieldTexts = {
    headerText: "Register as Protester/Intervenor Representative",
    contactInfo: "Protester/Intervenor Representative Info.",
    nameOfFirm: "",
    companyAddress: "Business Address",
    emailToolTip: "Enter an valid email address. It will be used as your User ID",
    prefixToolTip: "(ex: Mr., Mrs., Dr., Professor, etc.)*",
    suffixToolTip: "* (e.g., Jr., Sr., III).",
    companyAddToolTip: "Please enter the Business address",
  }

  agencyFieldTexts = {
    headerText: "Register as an Agency Representative",
    contactInfo: "Agency Representative Info",
    nameOfFirm: "Please Select the Government Agency you are representing",
    companyAddress: "Business Address",
    emailToolTip: "Enter an email address with a valid extension i.e; gov or .mil",
    prefixToolTip: "",
    suffixToolTip: "",
    companyAddToolTip: "Please enter the Agency address",
  }

  fieldTexts: any = { headerText: "Profile" };

  constructor(private activatedRoute: ActivatedRoute,
    public stateService: StateService,
    private httpService: HttpService, private router: Router,
    public util: UtilService, private location: Location, private userService: UserService) {
  }

  ngOnInit(): void {
    this.userId = this.activatedRoute.snapshot.params['user_Id'];
    this.register = this.activatedRoute.snapshot.data['register'];
    this.accountTypeId = this.activatedRoute.snapshot.params['accountTypeId'];
    if (this.register) {
      if (this.UserRole.Protester === Number(this.accountTypeId)) {
        this.fieldTexts = { ...this.protesterFieldTexts };
      } else if (this.UserRole.Agency_Attorney === Number(this.accountTypeId)) {
        this.fieldTexts = { ...this.agencyFieldTexts };
      }
      this.userProfile = <User>{ role_id: this.accountTypeId };
      this.disableForm = false;
    } else {
      this.getUserProfile();
    }
  }

  fillAddressForm(place: PlaceResult): void {
    let streetAddr = place.formatted_address?.split(",")[0]
    if (place.name !== streetAddr) {
      streetAddr = place.name + "," + streetAddr;
    }
    this.profileForm.controls['address1'].patchValue(streetAddr);

    place.address_components?.forEach(component => {
      const types = component.types;
      if (types.includes('locality')) {
        this.profileForm.controls['city'].patchValue(component.long_name);
      } else if (types.includes('administrative_area_level_1')) {
        this.profileForm.controls['state'].patchValue(component.long_name);
      } else if (types.includes('country')) {
        this.profileForm.controls['country'].patchValue(component.long_name);
      } else if (types.includes('postal_code')) {
        this.profileForm.controls['zipCode'].patchValue(component.long_name);
      }
    });
  }

  getUserProfile() {
    let userProfileUrl;
    if (this.userId) {
      userProfileUrl = '/get-user-info/' + this.userId;
    } else {
      userProfileUrl = '/user-profile-view';
    }

    this.httpService.Get<UserProfileResponse>(userProfileUrl).subscribe((response: UserProfileResponse) => {
      this.savedProfile = this.userProfile = response.user_Info;

      if (this.userProfile.role_id == UserRole.Agency_Attorney || this.userProfile.role_id == UserRole.Agency_Admin) {
        this.firmNameLabel = "Name of Agency";
      }
      this.disableForm = false;
    })
  }

  getRequestInfo(request: any) {
    if (this.register) {
        request.email = btoa(request.email);
    } else {
      request.isGAO_User = "N";
      request.isUserSearchEdit = this.userId ? "Y" : "N";
      request.old_email = this.userProfile?.email;
    }
    request.role_id = this.userProfile?.role_id;
    request.epds_role_id = request.epds_role_id ? request.epds_role_id : this.userProfile?.role_id;
    if (request.epds_role_id == UserRole.Agency_Attorney || request.epds_role_id == UserRole.Agency_Admin) {
      request.tier1_agency_id = this.selectedAgencies?.tier1Agency?.agency_Id;
      request.tier2_agency_id = this.selectedAgencies?.tier2Agency?.agency_Id;
    }
  }

  onSubmit(request: any) {
    this.profileSaving = true;
    this.getRequestInfo(request);
    if (this.register) {
      const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
      const options = { ...headers };
      this.httpService.Post<ValidateNewUser>('/user/validate-register-form',  request, options)
        .pipe(
          catchError(err => {
            return throwError(err);
          })
        ).subscribe((response) => {
        if (response.inputErrors) {
          let message = ' ';
          response.inputErrors.forEach(inputErrors =>  message += inputErrors.fieldName + ", with value: " + inputErrors.invalidValue + ", " + inputErrors.message + '<br/>');
          this.util.displayDialogMessage('Error', message);
        } else if (response.isSuccess) {
          this.submitRegister(request);
        }
      });
    } else {
      this.submitProfile(request);
    }
  }
  /*doRegister(apiRequest: RequestInfo) {

    this.util.displayDialogMessage('Rules of Behavior', "1. PURPOSE. This notice is to ensure that users of EPDS abide by security requirements and procedures needed to protect EPDS and customer information resources. It is also intended to help raise security awareness and inform system users about security policies and procedures. </br>" +
      "</br>" +
      "2. National policy requirements regarding information systems are stated in the Federal Information Security Management Act (FISMA) (Title III of the E-Government Act of 2002); the Computer Fraud and Abuse Act (18 U.S.C. Sec. 1030 [1993]); Office of Management and Budget (OMB) Circular No. A-123, Management Accountability and Control; and OMB Circular A-130, Management of Federal Information Resources.</br>" +
      "</br>" +
      "3. This notice applies to EPDS system users.</br>" +
      "</br>" +
      "4. UNDERSTANDING AND AGREEMENTS. As a user of EPDS, I:</br>" +
      "</br>" +
      "<ul><li>Will use EPDS only for authorized purposes.</li></ul>" +
      "<ul><li>Understand that information processed on this site may be monitored.</li></ul>" +
      "<ul><li>Will protect the EPDS system and all sensitive information contained in the system from unauthorized personnel.</li></ul>" +
      "<ul><li>Will process only data that pertains to official business and is authorized to be processed on the system. I will not retrieve information for someone who does not have authority to access the information. I will not intentionally access, delete, or alter files, operating systems or programs.</li></ul>" +
      "<ul><li>Acknowledge that I will receive user identifiers (user IDs) and passwords to authenticate my computer account. After receiving them, I will:</li></ul>" +
      "<ul><li>Protect and not share or publicly post my password.</li></ul>" +
      "<ul><li>Not knowingly permit or cause my username and password to be used by anyone other than myself or my authorized agent.</li></ul>" +
      "<ul><li>Report to GAO if my password has been compromised.</li></ul>" +
      "<ul><li>Be responsible for all activity that occurs on my individual account once my password has been used to log on.</li></ul>" +
      "<ul><li>Ensure my password meets EPDS system complexity requirements.</li></ul>" +
      "<ul><li>Will use due care when adding a co-representative (when applicable).</li></ul>" +
      "<ul><li>Will use anti-virus software to scan all files for malicious software (e.g., viruses, worms, etc.) before uploading any documents into the EPDS system.</li></ul>" +
      "<ul><li>Will not try to disable or subvert EPDS security controls or monitoring mechanisms.</li></ul>" +
      "<ul><li>Will ensure that the Web browser window is closed before navigating to other sites.</li></ul>" +
      "<ul><li>Understand that any person who obtains information from a computer connected to the Internet in violation of her employer’s computer-use restrictions is in violation of the Computer Fraud and Abuse Act.</li></ul>" +

      "5. EFFECTIVE DATE. This agreement becomes effective by the date of your electronic acceptance of the terms of this notice." +
      "</br>" +
      "</br>" +
      "ACCEPTANCE " +
      "</br>" +
      "I have read and understand the above Rules of Behavior. By my electronic acceptance , I acknowledge and agree that my access to the EPDS systems is covered by, and subject to, such rules. Further, I understand that unauthorized or inappropriate use of the EPDS system may result in the loss or limitation of my privilege and that GAO retains the right, at its sole discretion, to terminate, cancel, or suspend my access rights to the EPDS system at any time, without notice. I also understand that I could lose access to the system, as well prosecution, penalties or financial liability, depending on the severity of the misuse.",
      {confirmText: "I Agree",  width: '1200px', height: '600px'}).pipe()
      .subscribe(res => {
        if (res) {
          let rulesRequest = {
            email: apiRequest.request.email
          };
          const params = this.httpService.createHttpParams(rulesRequest);
          const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
          const options = { ...headers };
          this.httpService.Post('/user/rulesOfBehavior', params, options)
            .pipe(
              catchError(err => {
                return throwError(err);
              })
            ).subscribe((response: any) => {
              if (response?.isSuccess) {
                this.submitRegister(apiRequest);
              }
            });
        }
      });
  }*/

  submitRegister(request: RequestInfo) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { ...headers };
    this.httpService.Post("/user/register", request, options)
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      ).subscribe((response: any) => {
        if (response.isSuccess) {
          this.util.displayDialogMessage('Success', "Your account has been successfully created! </br> A temporary password has been sent to your provided email address. </br> Please use this temporary password to log into the system and create a new password. If you have not received your temporary password, please check your spam folder." +
            "</br> </br>" +
            "<b>Please note that you should immediately receive the temporary password email. If you do not receive the email, please check your “Junk E-Mail” or “SPAM” folder. Emails generated by EPDS will be sent from epds@epds.gao.gov</b>",
            { confirmText: "OK" , width: "600px" }).pipe()
            .subscribe(res => {
              if (res) {
                this.router.navigate(['/'] );
              }
            });
        }
      });
  }

  submitProfile(request: any) {
      this.userService.saveUser(request)
      .pipe(
        finalize(() => { this.profileSaving = false }),
        catchError(err => {
          return throwError(err);
        })
      ).subscribe(response => {
        this.getUserProfile();
        this.profileForm.form.markAsPristine();
        this.profileForm.form.markAsUntouched();
        this.agencyComponent?.form.form.markAsPristine();
        this.agencyComponent?.form.form.markAsUntouched();
      });
  }

  onCancel() {
    this.location.back();
  }

  onSelectedAgencies(event: any) {
    this.selectedAgencies = event;
    let agency1 = this.selectedAgencies.tier1Agency.agency_Name;
    let agency2 = this.selectedAgencies.tier2Agency?.agency_Name;
    const firmName = agency1?.concat(agency2 ? " / " + agency2 : "");
    this.userProfile.firm_Name = firmName;
  }

  onReturn() {
    this.router.navigate(['/'] );
  }
}
